import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import logoConstructora from '../../../../assets/img/logo_constructora.png';
import { formatDate, formatPercentage } from '../../../../utils/formatUtils';
import { getLotReferenceLabel } from './lotsUtils';

const FALLBACK_VALUE = '-';

const toText = (value, fallback = FALLBACK_VALUE) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const formatDateCell = (value) => formatDate(value, { fallback: FALLBACK_VALUE });

const formatCompletion = (value) =>
  formatPercentage(value, {
    decimals: Number.isInteger(Number(value)) ? 0 : 2,
    fallback: '0%',
  });

const formatSubscriberName = (subscriber) => {
  const firstName = String(subscriber?.first_name || '').trim();
  const lastName = String(subscriber?.last_name || '').trim();
  return `${firstName} ${lastName}`.trim();
};

const getProjectForLot = (lot, projectsMap = {}) => lot?.project || projectsMap[lot?.uuid] || null;

const getContractForLot = (lot, contractsById = {}) =>
  lot?.contract || contractsById?.[lot?.uuid_contract] || null;

const getContractSubscribers = (lot, contractsById = {}) => {
  const contract = getContractForLot(lot, contractsById);
  const subscribers = Array.isArray(contract?.subscribers) ? contract.subscribers : [];
  return subscribers
    .map(formatSubscriberName)
    .filter(Boolean);
};

const getContractNumberLabel = (lot, contractsById = {}) => {
  const contract = getContractForLot(lot, contractsById);
  return contract?.contract_number ? `#${contract.contract_number}` : FALLBACK_VALUE;
};

const getConstructionTypeName = (lot, constructionTypeNamesById = {}) =>
  toText(lot?.construction_type_name || constructionTypeNamesById?.[lot?.uuid_construction_type]);

const getLotExportRow = (
  lot,
  {
    projectsMap = {},
    contractsById = {},
    allotmentsById = {},
    constructionTypeNamesById = {},
  } = {}
) => {
  const project = getProjectForLot(lot, projectsMap);
  const subscribers = getContractSubscribers(lot, contractsById);
  const progress = Number(lot?.project_progress_summary?.completion_percentage ?? 0);
  const quality = Number(lot?.project_quality_summary?.completion_percentage ?? 0);
  const identifier = `${toText(lot?.block_number)} - ${toText(lot?.lot_number)}`;

  return {
    Loteo: toText(allotmentsById?.[lot?.uuid_allotment]?.name),
    ID: identifier,
    Estado: toText(getLotReferenceLabel({ lot, project })),
    'Tipo de construccion': getConstructionTypeName(lot, constructionTypeNamesById),
    Proyecto: toText(project?.name),
    'Fecha inicio obra': formatDateCell(project?.start_date),
    'Fecha fin obra': formatDateCell(project?.end_date),
    'Fecha estimada de entrega': formatDateCell(lot?.estimated_delivery_date),
    Contrato: getContractNumberLabel(lot, contractsById),
    Suscriptores: subscribers.length > 0 ? subscribers.join(' | ') : FALLBACK_VALUE,
    Subcontratista: toText(project?.subcontractor_name),
    'Progreso de obra': formatCompletion(progress),
    'Calidad de obra': formatCompletion(quality),
  };
};

const computeColumnWidths = (rows = []) => {
  if (!rows.length) return [];
  const headers = Object.keys(rows[0]);
  return headers.map((header) => {
    const maxValueLength = rows.reduce((max, row) => {
      const currentLength = String(row?.[header] ?? '').length;
      return Math.max(max, currentLength);
    }, header.length);
    return { wch: Math.min(Math.max(maxValueLength + 2, 12), 60) };
  });
};

const buildTimestamp = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${yyyy}${mm}${dd}_${hh}${min}`;
};

const loadImageAsset = async (imageUrl) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const dimensions = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth || 1, height: image.naturalHeight || 1 });
    image.onerror = reject;
    image.src = dataUrl;
  });

  return { dataUrl, ...dimensions };
};

const drawSectionTitle = (doc, title, x, y) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.setTextColor(0, 0, 0);
  doc.text(title, x, y);
  doc.setLineWidth(0.3);
  doc.setDrawColor(0, 0, 0);
  doc.line(x, y + 1.5, x + 70, y + 1.5);
};

const drawField = (doc, label, value, x, y, maxWidth = 82) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(102, 102, 102);
  doc.text(label, x, y);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(28, 28, 28);
  const lines = doc.splitTextToSize(toText(value), maxWidth);
  doc.text(lines, x, y + 5.5);
  return 5.5 + lines.length * 5;
};

export const exportLotsCompleteDatabaseToExcel = ({
  lots = [],
  projectsMap = {},
  contractsById = {},
  allotmentsById = {},
  constructionTypeNamesById = {},
} = {}) => {
  const rows = lots.map((lot) =>
    getLotExportRow(lot, {
      projectsMap,
      contractsById,
      allotmentsById,
      constructionTypeNamesById,
    })
  );

  if (!rows.length) return false;

  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = computeColumnWidths(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Lotes');
  XLSX.writeFile(workbook, `base_lotes_completa_${buildTimestamp()}.xlsx`);
  return true;
};

export const exportSingleLotToPdf = async (
  lot,
  {
    project = null,
    contract = null,
    subscribers = [],
    allotmentName = '',
    constructionTypeName = FALLBACK_VALUE,
    progressValue = 0,
    qualityValue = 0,
  } = {}
) => {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const projectStateLabel = getLotReferenceLabel({ lot, project });
  const identifier = `${toText(lot?.block_number)} - ${toText(lot?.lot_number)}`;
  const titleX = 20;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 297, 'F');

  try {
    const logo = await loadImageAsset(logoConstructora);
    const maxLogoWidth = 54;
    const maxLogoHeight = 18;
    const scale = Math.min(maxLogoWidth / logo.width, maxLogoHeight / logo.height);
    const logoWidth = Math.max(18, logo.width * scale);
    const logoHeight = Math.max(8, logo.height * scale);
    const logoX = 194 - logoWidth;
    const logoY = 12;

    doc.addImage(logo.dataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
  } catch (_error) {
    // Si el logo falla no se interrumpe la descarga del PDF.
  }

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(19);
  doc.text('Ficha de Lote', titleX, 21);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Generado: ${formatDate(new Date(), { fallback: FALLBACK_VALUE })}`, titleX, 27);
  doc.setLineWidth(0.6);
  doc.setDrawColor(0, 0, 0);
  doc.line(16, 32, 194, 32);

  doc.setDrawColor(170, 170, 170);
  doc.roundedRect(12, 38, 186, 246, 3, 3, 'S');

  let leftY = 50;
  let rightY = 50;
  const leftX = 20;
  const rightX = 110;

  drawSectionTitle(doc, 'Datos del lote', leftX, leftY);
  leftY += 8;
  leftY += drawField(doc, 'Loteo', allotmentName, leftX, leftY);
  leftY += drawField(doc, 'ID', identifier, leftX, leftY);
  leftY += drawField(doc, 'Estado', projectStateLabel, leftX, leftY);
  leftY += drawField(doc, 'Tipo de construccion', constructionTypeName, leftX, leftY);
  leftY += drawField(doc, 'Fecha estimada de entrega', formatDateCell(lot?.estimated_delivery_date), leftX, leftY);

  drawSectionTitle(doc, 'Contrato y suscriptores', rightX, rightY);
  rightY += 8;
  rightY += drawField(
    doc,
    'Contrato',
    contract?.contract_number ? `#${contract.contract_number}` : FALLBACK_VALUE,
    rightX,
    rightY
  );

  const subscriberLabel =
    subscribers.length > 0 ? subscribers.map((subscriber) => formatSubscriberName(subscriber)).join(', ') : FALLBACK_VALUE;
  rightY += drawField(doc, 'Suscriptores', subscriberLabel, rightX, rightY, 78);

  drawSectionTitle(doc, 'Obra y calidad', leftX, Math.max(leftY, rightY) + 8);
  let metricsY = Math.max(leftY, rightY) + 16;
  metricsY += drawField(doc, 'Proyecto', project?.name || FALLBACK_VALUE, leftX, metricsY, 170);
  metricsY += drawField(doc, 'Inicio de obra', formatDateCell(project?.start_date), leftX, metricsY);
  metricsY += drawField(doc, 'Fin de obra', formatDateCell(project?.end_date), leftX, metricsY);
  metricsY += drawField(doc, 'Subcontratista', project?.subcontractor_name, leftX, metricsY, 170);
  metricsY += drawField(doc, 'Progreso de obra', formatCompletion(progressValue), leftX, metricsY);
  drawField(doc, 'Calidad de obra', formatCompletion(qualityValue), leftX, metricsY);

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.8);
  doc.line(20, 270, 190, 270);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text('Constructora Colonial', 20, 276);

  const filename = `lote_${toText(lot?.lot_number, 'sin_numero')}_${buildTimestamp()}.pdf`;
  doc.save(filename);
};

export const getLotSubscribersSummary = (lot, contractsById = {}) => {
  const subscribers = getContractSubscribers(lot, contractsById);
  if (!subscribers.length) {
    return {
      firstLabel: FALLBACK_VALUE,
      extraCount: 0,
      tooltipText: '',
    };
  }

  return {
    firstLabel: subscribers[0],
    extraCount: Math.max(subscribers.length - 1, 0),
    tooltipText: subscribers.join('\n'),
  };
};

export const getLotResolvedContractNumber = (lot, contractsById = {}) =>
  getContractNumberLabel(lot, contractsById);

export const getLotResolvedProject = (lot, projectsMap = {}) => getProjectForLot(lot, projectsMap);

export const getLotResolvedConstructionType = (lot, constructionTypeNamesById = {}) =>
  getConstructionTypeName(lot, constructionTypeNamesById);
